import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ApexChart,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTitleSubtitle,
  ChartComponent,
} from 'ng-apexcharts';

type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  colors: string[];
  plotOptions: ApexPlotOptions;
  grid: ApexGrid;
};

interface DataPointSelectionConfig {
  dataPointIndex: number;
  seriesIndex: number;
  [key: string]: any; // To allow additional properties if needed
}

@Component({
  selector: 'app-analytics-apex-chart-pie',
  templateUrl: './analytics-apex-chart-pie.component.html',
  styleUrls: ['../analytics.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AnalyticsApexChartPieComponent implements OnInit, OnChanges {
  @ViewChild('chart') chart: ChartComponent;

  public chartOptions: Partial<ChartOptions>;

  @Input() series: number[] = [];

  @Input() labels: string[] = [];

  @Input() manualColors: string[] = [];

  @Input() title: string = 'Pie Chart';

  @Output() categoryChange = new EventEmitter<string>();

  ngOnInit(): void {
    this.setOptions();
  }

  ngOnChanges(): void {
    this.setOptions();
  }

  onChangeCategory(config: DataPointSelectionConfig): void {
    const selectedPointIndex = config.dataPointIndex;
    const selectedlabel = this.chartOptions.labels[selectedPointIndex];
    this.categoryChange.emit(selectedlabel.toLowerCase());
  }

  setOptions(): void {
    this.chartOptions = {
      series: this.series,
      chart: {
        height: 360,
        type: 'pie',
        width: 230,
        parentHeightOffset: 0,
        events: {
          dataPointSelection: (
            event: any,
            chartContext: any,
            config: DataPointSelectionConfig,
          ) => {
            if (this.categoryChange.observed) {
              this.onChangeCategory(config);
            }
          },
        },
      },
      plotOptions: {
        pie: {
          customScale: 1.0, // fills more of the box
          expandOnClick: false,
        },
      },
      labels: this.labels,
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
      },
      grid: { padding: { top: 8, bottom: 28 } },
      title: {
        text: this.title,
        align: 'center',
      },
      responsive: [
        {
          breakpoint: 1520, // smaller desktop
          options: {
            chart: { width: 230 },
          },
        },
        {
          breakpoint: 1024, // tablet
          options: {
            chart: { width: 480, height: 320 },
          },
        },
        {
          breakpoint: 768, // large mobile
          options: {
            chart: { width: 700, height: 280 },
            legend: {
              show: true,
              position: 'bottom',
              fontSize: '11px',
              markers: { width: 10, height: 10 },
              itemMargin: { vertical: 2, horizontal: 6 },
            },
          },
        },
        {
          breakpoint: 640, // medium mobile
          options: {
            chart: { height: 260 },
            legend: {
              show: true,
              position: 'bottom',
              fontSize: '11px',
              markers: { width: 10, height: 10 },
              itemMargin: { vertical: 2, horizontal: 6 },
            },
            dataLabels: { style: { fontSize: '10px' } },
          },
        },
        {
          breakpoint: 480, // small mobile
          options: {
            chart: { width: 370, height: 260 },
            legend: {
              itemMargin: { vertical: 2, horizontal: 4 },
            },
          },
        },
      ],
      colors: this.manualColors.length > 0 ? this.manualColors : [
        '#ef4732',
        '#ff742e',
        '#3dcc64',
        '#7a4b8a',
        '#e7f9fc',
        '#ffbb2c',
        '#17328d',
        '#3e95dc',
        '#8793a2',
        '#365fbf',
        '#81548f',
      ],
    };
  }
}
